// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  NonLinearWizard,
  Step,
} from "@components";
import { getSchema } from "@utils/schema";
import Step1Configure from "./step1-configure";
import Step2Pattern from "./step2-pattern";
import type {
  NewShiftPattern,
  ShiftPattern,
  DraftShiftPattern,
} from "@modules/service/reducer/shift-patterns/types";

type OwnProps = $Exact<{
  defaultStep: 1 | 2,
  editing: boolean,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  shiftPattern: ?ShiftPattern,
  draft: ?DraftShiftPattern,
}>;

type PropsFromDispatch = $Exact<{
  setShiftPattern: typeof actionCreators.SetShiftPattern,
  addShiftPattern: typeof actionCreators.AddShiftPattern,
  updateShiftPattern: typeof actionCreators.UpdateShiftPattern,
  setShowAddModal: typeof actionCreators.ShowAddShiftPatternModal,
  setShowEditModal: typeof actionCreators.ShowEditShiftPatternModal,
  setDraft: typeof actionCreators.UpdateShiftPatternDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
}>;

export class ShiftPatternAddModal extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    const schema = getSchema("ShiftPatternDTO");
    this.validate = new Ajv().compile(schema);
  }

  get isValid() {
    if (!this.validate) {
      return false;
    }

    const pattern = this.props.draft || this.props.shiftPattern;
    if (!pattern || !pattern.shiftPatternDays.length) {
      return false;
    }
    const res = this.validate(this.props.draft || this.props.shiftPattern);
    return res;
  }

  handleCancel = () => {
    this.resetState();
    this.props.editing ? this.props.setShowEditModal(false) : this.props.setShowAddModal(false);
  };

  handleSubmit = () => {
    const { draft, shiftPattern } = this.props;
    if (!draft) return;
    const data = {
      ...shiftPattern,
      ...(draft.id ? { id: draft.id } : {}),
      name: draft.name,
      shiftPatternDays: draft.shiftPatternDays.slice(0, draft.days),
    };

    this.props.setShiftPattern(data);

    if (this.props.editing) {
      this.props.updateShiftPattern({
        clientId: this.props.clientId,
        data,
      });
      return;
    }

    this.props.addShiftPattern({ clientId: this.props.clientId, data });
  };

  resetState = () => {
    this.props.setDraft(null);
  };

  get stepData() {
    return [
      {
        data: {},
        valid: this.props.editing,
      },
      {
        data: {},
        valid: this.props.editing,
      },
    ];
  }

  render() {
    return (
      <Modal className="modal-normal">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.editing
            ? this.props.t("SHIFT_PATTERN_EDIT_TITLE")
            : this.props.t("SHIFT_PATTERN_ADD_TITLE")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column justify-content-start align-content-center">
          <NonLinearWizard defaultStep={this.props.defaultStep} data={this.stepData}>
            <Step
              component={Step1Configure}
              title={this.props.t("SHIFT_PATTERN_CONFIGURE")}
              rightInfo={item => item.name}
            />
            <Step
              component={Step2Pattern}
              title={this.props.t("SHIFT_PATTERN_SPECIFY_PATTERN")}
              stepData={this.stepData[1].data}
            />
          </NonLinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end gap-1">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button materialIcon="save" disabled={!this.isValid} onMouseUp={this.handleSubmit}>
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    shiftPattern: serviceSelectors.getCurrentShiftPattern(state),
    draft: serviceSelectors.getShiftPatternDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setShiftPattern: actionCreators.SetShiftPattern,
  addShiftPattern: actionCreators.AddShiftPattern,
  updateShiftPattern: actionCreators.UpdateShiftPattern,
  setShowAddModal: actionCreators.ShowAddShiftPatternModal,
  setShowEditModal: actionCreators.ShowEditShiftPatternModal,
  setDraft: actionCreators.UpdateShiftPatternDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(ShiftPatternAddModal);
